/**
 * MIME → Lucide icon resolution for attachment surfaces that show a glyph
 * instead of a preview (inline file chips, the attachment lightbox).
 *
 * Extracted from `CardFileEmbedView` so the chip and the lightbox it opens
 * agree on the glyph — a chip showing a spreadsheet icon that opens a
 * lightbox showing a generic page reads as two different files.
 *
 * Content type is the primary signal and the filename extension is the
 * fallback, because the BE stores whatever the browser reported at upload
 * time: that is occasionally absent, and for less common types (`.md`,
 * `.7z`) it is often the useless `application/octet-stream`.
 *
 * Note: chat has its own finer-grained map in `MessageAttachments.tsx`
 * (it distinguishes Word / PowerPoint / JSON). Consolidating the two is
 * unblocked cleanup, deliberately not done here — chat's surface is not
 * part of this change and its map is a superset, not a conflict.
 */

import {
  File,
  FileArchive,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  type LucideIcon,
} from 'lucide-react'

export const isImageContentType = (contentType: string | null | undefined): boolean =>
  typeof contentType === 'string' && contentType.startsWith('image/')

export function resolveFileIcon(mimeType: string | null | undefined, fileName: string): LucideIcon {
  if (mimeType) {
    if (mimeType.startsWith('image/')) return FileImage
    if (mimeType.startsWith('video/')) return FileVideo
    if (mimeType.startsWith('audio/')) return FileAudio
    if (mimeType === 'application/pdf') return FileText
    if (mimeType.startsWith('text/')) return FileText
    if (
      mimeType === 'application/zip' ||
      mimeType === 'application/x-zip-compressed' ||
      mimeType === 'application/x-tar' ||
      mimeType === 'application/gzip'
    ) {
      return FileArchive
    }
    if (
      mimeType === 'application/vnd.ms-excel' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      mimeType === 'text/csv'
    ) {
      return FileSpreadsheet
    }
  }
  const ext = fileName.split('.').pop()?.toLowerCase() ?? ''
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'heic'].includes(ext)) return FileImage
  if (['mp4', 'webm', 'mov', 'mkv'].includes(ext)) return FileVideo
  if (['mp3', 'wav', 'm4a'].includes(ext)) return FileAudio
  if (['pdf', 'txt', 'md'].includes(ext)) return FileText
  if (['zip', 'tar', 'gz', '7z'].includes(ext)) return FileArchive
  if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet
  return File
}
